import styled from 'styled-components'
import { COLOR, FONT_SIZE, IMG } from '../../../../../themes'
import { THEME_COLORS, hexToRgba } from '../../../../../helpers'

const mainColor = props => props.colorTheme ? props.colorTheme.MAIN : COLOR.GOLD_1
const onMainColor = props => props.colorTheme ? props.colorTheme.FONT_COLOR.SINGLE_TONE : COLOR.BLACK

const getPalette = (colorTheme = THEME_COLORS.GOLD) => ({
  primary: colorTheme.MAIN,
  primaryHover: hexToRgba(colorTheme.MAIN, 0.85),
  onPrimary: colorTheme.FONT_COLOR.SINGLE_TONE,
  onPrimaryStrong: colorTheme.FONT_COLOR.SINGLE_TONE,
  hero: colorTheme.BG_OPACITY,
  track: hexToRgba(colorTheme.MAIN, 0.3),
  fill: colorTheme.MAIN,
  soft: colorTheme.BG_OPACITY,
  softText: '#404040'
})

const palette = key => props => getPalette(props.colorTheme)[key]

export const AttendeeCheckInButtonStyled = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  border: 1px solid ${mainColor};
  border-radius: 4px;
  background-color: ${mainColor};
  font-size: ${FONT_SIZE.XXS};
  font-family: NunitoRegular;
  color: ${onMainColor};
  cursor: pointer;

  .attendee-check-in-count {
    min-width: 22px;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.7);
    font-family: NunitoSansSemiBold;
    font-size: 12px;
    color: #171717;
  }

  &.complete .attendee-check-in-count {
    background: #D1FAE5;
    color: #047857;
  }
`

export const GuestCheckInStatusStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 54px;
  padding: 18px 20px;
  border: 1px solid #E5E5E5;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  font-family: NunitoSansRegular;
  line-height: 20px;

  .guest-check-in-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${palette('soft')};
    font-family: NunitoSansSemiBold;
    font-size: 12px;
    color: ${palette('softText')};
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .guest-check-in-title {
    flex: 1;
    font-size: 14px;
    color: #171717;
    white-space: nowrap;
  }

  .guest-check-in-status {
    font-size: 13px;
    color: #737373;
    text-align: right;
  }

  .guest-check-in-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border: 1px solid #BBF7D0;
    border-radius: 999px;
    background: #F0FDF4;
    font-family: NunitoSansSemiBold;
    font-size: 12px;
    line-height: 16px;
    color: #15803D;
    white-space: nowrap;
  }

  .guest-check-in-badge-icon {
    width: 12px;
    height: 12px;
    background-color: currentColor;
    mask: url(${IMG['check.svg']}) center / contain no-repeat;
    -webkit-mask: url(${IMG['check.svg']}) center / contain no-repeat;
  }

  &.checked-in .guest-check-in-avatar {
    background: #F0FDF4;
    color: #15803D;
  }
`

export const AttendeeCheckInModalStyled = styled.div`
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 48px);
  font-family: NunitoSansRegular;
  color: #171717;

  .attendee-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 24px 16px 24px;
  }

  .attendee-title {
    font-family: LibreBaskervilleBold;
    font-size: 26px;
    color: #171717;
  }

  .attendee-close {
    cursor: pointer;
  }

  .attendee-hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    min-height: 84px;
    margin: 0 24px 16px 24px;
    padding: 18px 20px;
    border-radius: 14px;
    background: ${palette('hero')};
    box-sizing: border-box;

    &.complete {
      background: #ECFDF5;
    }
  }

  .attendee-progress {
    display: flex;
    align-items: center;
    gap: 18px;
    flex: 1;
    min-width: 0;
  }

  .attendee-count {
    white-space: nowrap;

    b {
      font-family: LibreBaskervilleBold;
      font-size: 34px;
      line-height: 1;
    }

    span {
      margin-left: 6px;
      font-size: 16px;
      color: #737373;
    }
  }

  .attendee-meter-wrap {
    flex: 1;
    min-width: 0;
  }

  .attendee-meter-label {
    margin-bottom: 8px;
    font-family: NunitoSansSemiBold;
    font-size: 14px;

    small {
      font-family: NunitoSansRegular;
      font-size: 13px;
      color: #737373;
    }
  }

  .attendee-meter {
    height: 8px;
    border-radius: 999px;
    background: ${palette('track')};
    overflow: hidden;

    div {
      height: 100%;
      border-radius: 999px;
      background: ${palette('fill')};
      transition: width 0.3s ease;
    }
  }

  .attendee-hero.complete {
    .attendee-meter-label {
      color: #047857;
    }

    .attendee-meter {
      background: #A7F3D0;

      div {
        background: #059669;
      }
    }
  }

  .attendee-window {
    margin-top: 8px;
    font-size: 13px;
    color: #737373;
  }

  .attendee-button {
    min-height: 40px;
    border-radius: 8px;
    border: 1.5px solid #E5E5E5;
    background: #FFFFFF;
    padding: 0 20px;
    font-family: NunitoSansSemiBold;
    font-size: 14px;
    color: #363636;
    cursor: pointer;
    white-space: nowrap;

    &.primary {
      border: none;
      background: ${palette('primary')};
      color: ${palette('onPrimary')};
      transition: background-color 0.15s ease;

      &:hover:not(:disabled) {
        background: ${palette('primaryHover')};
      }
    }

    &.large {
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 48px;
      padding: 0 22px;
      font-size: 15px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .attendee-button-count {
    min-width: 22px;
    padding: 2px 6px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    color: #171717;
    text-align: center;
    box-sizing: border-box;
  }

  .attendee-confirm {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;

    strong {
      font-family: NunitoSansSemiBold;
      font-size: 16px;
    }

    p {
      margin: 4px 0 0 0;
      font-size: 14px;
      color: #737373;
    }
  }

  .attendee-confirm-actions {
    display: flex;
    gap: 10px;
  }

  .attendee-tools {
    display: flex;
    gap: 12px;
    padding: 0 24px 8px 24px;
  }

  .attendee-search {
    flex: 1;
    min-width: 0;
    height: 40px;
    border-radius: 8px;
    border: 1.5px solid #E5E5E5;
    padding: 0 14px;
    font-family: NunitoSansRegular;
    font-size: 15px;
    box-sizing: border-box;
    outline: none;

    &:focus {
      border-color: ${palette('primary')};
    }
  }

  .attendee-filters {
    display: flex;
    padding: 3px;
    border-radius: 8px;
    background: #F5F5F5;
  }

  .attendee-filter {
    border: none;
    border-radius: 6px;
    background: transparent;
    padding: 0 12px;
    font-family: NunitoSansRegular;
    font-size: 13px;
    color: #737373;
    cursor: pointer;
    white-space: nowrap;

    &.active {
      background: #FFFFFF;
      font-family: NunitoSansSemiBold;
      color: #171717;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
  }

  .attendee-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 24px 24px 24px;
  }

  .attendee-row {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) 140px 270px;
    align-items: center;
    gap: 12px;
    min-height: 58px;
    padding: 9px 0;
    border-bottom: 1px solid #F0F0F0;
  }

  .attendee-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${palette('soft')};
    font-family: NunitoSansSemiBold;
    font-size: 12px;
    color: ${palette('softText')};
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .attendee-name {
    font-size: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .attendee-owner {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 999px;
    background: ${palette('soft')};
    color: ${palette('softText')};
    font-family: NunitoSansSemiBold;
    font-size: 11px;
    vertical-align: middle;
  }

  .attendee-pin {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 18px;
    letter-spacing: 2px;
  }

  .attendee-copy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: none;
    cursor: pointer;

    &:hover {
      background: ${palette('soft')};
    }

    &.copied {
      background: #D1FAE5;
    }
  }

  .attendee-copy-icon {
    width: 16px;
    height: 16px;
    background-color: ${palette('primary')};
    mask: url(${IMG['ic_copy.svg']}) center / contain no-repeat;
    -webkit-mask: url(${IMG['ic_copy.svg']}) center / contain no-repeat;
  }

  .attendee-status {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    font-size: 13px;
    color: #737373;
    text-align: right;
    white-space: nowrap;
  }

  .attendee-guest-button {
    min-width: 96px;
    height: 36px;
    padding: 0 16px;
    border-radius: 8px;
    border: 1.5px solid ${palette('primary')};
    background: #FFFFFF;
    font-family: NunitoSansSemiBold;
    font-size: 14px;
    color: #171717;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: ${palette('hero')};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .attendee-guest-confirm {
    background: ${palette('primary')};
    font-family: NunitoSansBold;
    color: ${palette('onPrimaryStrong')};

    &:hover:not(:disabled) {
      background: ${palette('primaryHover')};
    }
  }

  .attendee-status-done {
    font-family: NunitoSansSemiBold;
    color: #047857;
  }

  .attendee-row.checked-in {
    .attendee-avatar {
      background: #D1FAE5;
      color: #047857;
    }

    .attendee-name {
      color: #737373;
    }
  }

  .attendee-row.declined {
    .attendee-avatar {
      background: #F5F5F5;
      color: #A3A3A3;
    }

    .attendee-name {
      color: #A3A3A3;
    }
  }

  .attendee-empty {
    padding: 32px 0;
    text-align: center;
    font-size: 14px;
    color: #737373;
  }
`

export const CheckinToastStyled = styled.div`
  position: fixed;
  bottom: 27px;
  left: 50%;
  transform: translateX(-50%) translateY(120%);
  width: 494px;
  max-width: calc(100vw - 32px);
  min-height: 82px;
  background: #000000;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px 20px;
  box-sizing: border-box;
  z-index: 999999;
  transition: transform 0.4s ease-out;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

  &.visible {
    transform: translateX(-50%) translateY(0);
  }

  .toast-header {
    display: flex;
    align-items: center;
    width: 100%;
    height: 24px;
  }

  .toast-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    margin-right: 14px;
  }

  .toast-title {
    flex: 1;
    font-family: NunitoSansSemiBold;
    font-size: 18px;
    line-height: 24px;
    color: #FFFFFF;
  }

  .toast-message {
    margin: 8px 0 0 38px;
    font-size: 14px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.7);
  }

  .toast-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-left: 12px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    opacity: 0.7;

    img {
      width: 11px;
      height: 11px;
    }

    &:hover {
      opacity: 1;
    }
  }
`
